import { useState, useEffect, useCallback } from "react";
import useOrderCheckoutApi from "@apis/useOrderCheckoutApi";
import useOptions from "@util/hook/useOptions";

/**
 * 循環訂單首購資料取得 Hook
 * 負責取得首購資料（循環訂單資訊、預設品項、發票設定、會員資訊、選項）
 *
 * 購物車狀態交由 <CartTable /> 自行管理
 *
 * @param {Object} options - 設定選項
 * @param {string|number} [options.cycleOrderID] - 循環訂單ID
 * @param {boolean} [options.isFetchOnMount=true] - 是否自動取得資料（預設 true）
 * @returns {Object} 首購資料
 */
/**
 * 將 API 回傳的首購品項，轉成 CartTable 可吃的格式
 * - API 用 unitPrice，CartTable 用 salePrice
 * - 保留 sku / productName / variantName / productTypeText 等送單需要的欄位
 */
const toCartItem = item => ({
	productID: item.productID,
	variantID: item.variantID,
	productName: item.productName,
	variantName: item.variantName || "",
	sku: item.sku || "",
	salePrice: item.unitPrice || 0,
	quantity: Number(item.quantity) || 0,
	productTypeText: item.productTypeText || "",
});

const useCyclePurchaseFirstOrder = ({ cycleOrderID, isFetchOnMount = true } = {}) => {
	const { getOrderCheckoutCyclePurchaseApi, getOrderCheckoutOptionsApi } = useOrderCheckoutApi();

	const { options } = useOptions({ getOptionsApi: getOrderCheckoutOptionsApi, isFetchOnMount: true });

	const [firstOrderData, setFirstOrderData] = useState({
		cycleOrderID: null,
		cycleOrderMain: null,
		items: null, // null = 尚未 fetch，[] = 已 fetch 但無品項
		invoiceSetting: null,
		member: null,
	});

	const fetchFirstOrderData = useCallback(
		async cycleOrderID => {
			if (!cycleOrderID) return;

			try {
				const resp = await getOrderCheckoutCyclePurchaseApi({ cycleOrderID });

				if (resp) {
					setFirstOrderData({
						cycleOrderID: resp.cycleOrderID || cycleOrderID,
						cycleOrderMain: resp?.cycleOrderMain || null,
						items: Array.isArray(resp.items) ? resp.items.map(toCartItem) : [],
						invoiceSetting: resp.invoiceSetting || null,
						member: resp.member || null,
					});
				}
			} catch (error) {
				console.error("取得首購資料失敗:", error);
			}
		},
		[getOrderCheckoutCyclePurchaseApi],
	);

	useEffect(() => {
		if (isFetchOnMount && cycleOrderID) {
			fetchFirstOrderData(cycleOrderID);
		}
		// eslint-disable-next-line
	}, [cycleOrderID, isFetchOnMount, fetchFirstOrderData]);

	return {
		cycleOrderID: firstOrderData.cycleOrderID,
		cycleOrderInfo: firstOrderData.cycleOrderMain,
		initialItems: firstOrderData.items,
		invoiceSetting: firstOrderData.invoiceSetting,
		member: firstOrderData.member,
		options,
		fetchFirstOrderData,
	};
};

export default useCyclePurchaseFirstOrder;
